import { useMemo, useCallback, useState } from 'react';
import get from 'lodash/get';
import classnames from 'classnames';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import VoteButton from '../vote-button';
import useLazyImage from '../../hooks/useLazyImage';
import config from '../../config';
import styles from './ProductDetails.module.scss';

// Thumbnail item component with lazy loading
function ThumbnailItem({ image, index, isActive, onClick }) {
  const thumbnailUrl = get(image, 'formats.thumbnail.url', image.url) || '';
  const { imageSrc, imageRef, isLoaded } = useLazyImage(thumbnailUrl);

  return (
    <div
      className={classnames(styles.thumbnailItem, isActive && styles.active)}
      onClick={onClick}
    >
      <img
        ref={imageRef}
        src={imageSrc}
        alt={`Thumbnail ${index + 1}`}
        className={isLoaded ? styles.loaded : ''}
        loading="lazy"
      />
    </div>
  );
}

// Featured image component with lazy loading
function FeaturedImage({ image, activeIndex, onClick }) {
  const imageUrl = get(image, 'formats.medium.url', image.url) || '';
  const { imageSrc, imageRef, isLoaded } = useLazyImage(imageUrl);

  return (
    <div
      className={styles.featuredImageContainer}
      id="featured-image-container"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <img
        ref={imageRef}
        src={imageSrc}
        alt=""
        id="featured-image"
        className={isLoaded ? styles.loaded : ''}
        loading="lazy"
      />
    </div>
  );
}

function ImageGallery({ images, onImageClick }) {
  const [activeIndex, setActiveIndex] = useState(0);

  const thumbnailItems = useMemo(() => {
    if (!images || images.length === 0) return null;
    return images.map((img, idx) => (
      <ThumbnailItem
        key={idx}
        image={img}
        index={idx}
        isActive={idx === activeIndex}
        onClick={() => setActiveIndex(idx)}
      />
    ));
  }, [images, activeIndex]);

  const handleFeaturedImageClick = () => {
    if (images && images[activeIndex]) {
      const fullImageUrl = get(images, `[${activeIndex}].url`, '');
      onImageClick(fullImageUrl);
    }
  };

  // Handle no images case - after all hooks
  if (!images || images.length === 0) {
    return (
      <div className={styles.galleryColumn}>
        <p>No images available</p>
      </div>
    );
  }

  return (
    <div className={styles.galleryColumn}>
      <div className={styles.galleryContainer}>
        <div className={styles.thumbnailsContainer}>
          {thumbnailItems}
        </div>
        {images && images.length > 0 && (
          <FeaturedImage
            image={images[activeIndex]}
            activeIndex={activeIndex}
            onClick={handleFeaturedImageClick}
          />
        )}
      </div>
    </div>
  );
}

function ProductDetails({ product, lang }) {
  const isEnglish = lang === 'en';
  const fieldPostfix = isEnglish ? 'EN' : 'TC';
  const teamNumber = get(product, 'ordering') ? `${get(product, 'ordering')} - ` : '';
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageUrl, setLightboxImageUrl] = useState('');

  const images = useMemo(() => {
    if (!product) return [];
    const ret = [];

    if (get(product, 'coverImage')) {
      ret.push(get(product, 'coverImage'));
    }

    if (Array.isArray(get(product, 'image'))) {
      for (const img of get(product, 'image')) {
        ret.push(img);
      }
    }

    return ret;
  }, [product]);

  const handleImageClick = useCallback((imageUrl) => {
    setLightboxImageUrl(imageUrl);
    setLightboxOpen(true);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  // Get field label from config.fieldTranslations
  const getFieldLabel = useCallback((fieldName) => {
    const translations = config.fieldTranslations || {};
    if (translations[fieldName] && translations[fieldName][fieldPostfix]) {
      return translations[fieldName][fieldPostfix] + ': ';
    }
    // Fallback to default labels
    const fallbackLabels = {
      school: isEnglish ? 'School Name: ' : '學校名稱: ',
      teamMember: isEnglish ? 'Team Members: ' : '團隊成員: ',
      topic: isEnglish ? 'Theme: ' : '主題: ',
      plan: isEnglish ? 'Project Name: ' : '計劃名稱: ',
      target: isEnglish ? 'Target Group: ' : '主要對象: ',
      problem: isEnglish ? 'Problem Statement: ' : '問題論述: ',
      solution: isEnglish ? 'Solution: ' : '解決方案: ',
      description: isEnglish ? 'Description: ' : '描述: ',
    };
    return fallbackLabels[fieldName] || '';
  }, [fieldPostfix, isEnglish]);

  const ProductDetailsInfoItem = useCallback(({ field }) => {
    if (!product) return null;
    const value = get(product, `${field}${fieldPostfix}`);
    if (!value || value === 'N/A') return null;

    const label = getFieldLabel(field);
    return (
      <div className={styles.infoItem}>
        <span className={styles.infoLabel}>{label}</span>
        <span className={styles.infoValue}>{typeof value === 'object' ? <BlocksRenderer content={value} /> : value}</span>
      </div>
    );
  }, [product, fieldPostfix, getFieldLabel]);

  // Handle missing product - after all hooks
  if (!product) {
    return (
      <div className={styles.productContainer}>
        <p>{isEnglish ? 'Product information not available.' : '產品資訊不可用。'}</p>
      </div>
    );
  }

  return (
    <div>
      <div id="product-content" className={styles.productContainer}>
        <ImageGallery images={images} onImageClick={handleImageClick} />

        <div className={styles.detailsColumn}>
          <h1 className={styles.productDetailsTitle}>
            {/* {teamNumber} */}
            {get(product, `title${fieldPostfix}`) || (isEnglish ? 'Untitled' : '無標題')}
          </h1>

          {/* Vote button */}
          <VoteButton
            votes={get(product, 'votes', 0)}
            documentId={get(product, 'documentId', '')}
            voteBatch={get(product, 'voteBatch', null)}
          />

          <div className={styles.productInfo}>
            <ProductDetailsInfoItem field="school" />
            <ProductDetailsInfoItem field="teamMember" />
            <ProductDetailsInfoItem field="topic" />
            {/* <ProductDetailsInfoItem field="plan" /> */}
            <ProductDetailsInfoItem field="target" />
            <ProductDetailsInfoItem field="problem" />
            <ProductDetailsInfoItem field="solution" />
            {/* <ProductDetailsInfoItem field="description" /> */}
          </div>

          {/* External link buttons */}
          {product.button && Array.isArray(product.button) && product.button.length > 0 && (
            <div className={styles.buttonContainer}>
              {product.button.map((btn) => (
                <a
                  key={btn.id}
                  href={btn.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.externalButton}
                >
                  {get(btn, `label${fieldPostfix}`)}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category Display - matches WordPress product-details.php */}
      {product && product.category && (
        <div className={styles.productCategoryDisplay}>
          <span>{isEnglish ? 'Category: ' : 'Category: '}</span>
          <a href={`${config.basePath || ''}/product-category/?id=${product.category.documentId}`}>
            {get(product, `category.name${fieldPostfix}`)}
          </a>
          {
            product.productCategories && Array.isArray(product.productCategories) && product.productCategories.length > 0 && (
              <>
                <span> | </span>
                {product.productCategories.map((cat, index) => (
                  <span key={cat.id}>
                    <a href={`${config.basePath || ''}/product-category/?pid=${cat.documentId}`}>
                      {get(cat, `name${fieldPostfix}`)}
                    </a>
                    {index < product.productCategories.length - 1 && <span>, </span>}
                  </span>
                ))}
              </>
            )
          }
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div className={styles.lightbox} onClick={handleCloseLightbox}>
          <span className={styles.closeLightbox} onClick={handleCloseLightbox}>
            &times;
          </span>
          <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <img src={lightboxImageUrl} alt="" />
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetails;
